// 분기(branch) 슬롯 탭 파생 — 캔버스 오버레이가 그릴 탭 바 목록을 순수 함수로 만든다.
//
// 배치 규칙: **branch마다 슬롯 탭 바 1개**를 자기 노드 rect 바로 위에 두고 그 branch의 **전체
// 슬롯을 상시** 보여준다(2026-08-31 확정 — 원래 방식. 같은 날 시도한 왼쪽 여백 열은 시선 왕복,
// 컴팩트 칩 + hover 확장은 선택지가 숨는 문제로 각각 되돌렸다). 한때(2026-07-03)
// 중첩 탭 겹침을 없애려고 "최상위 branch rect 한 곳에 활성 경로 체인(`[슬롯A|슬롯B] → [슬롯C|슬롯D]
// → …`) 1개"로 몰아 놓았는데, root branch가 문서 전체를 감싸는 템플릿(TBM)에서는 모든 분기 탭이 문서
// 맨 위로 몰려 어느 탭이 어느 노드 것인지 알 수 없었다. 겹침은 전면 체인화가 아니라 **겹칠 때만
// 밀어내기**(branchTabLayout)로 푼다 — 그쪽이 탭과 노드의 대응을 보존한다.
//
// 열거 범위는 "캔버스에 실제로 렌더되는 branch" = 활성 경로다. 편집 캔버스는 조건을 평가하지 않고
// 활성 슬롯 children만 렌더하므로, 비활성 슬롯 속 branch는 DOM에 없고 탭을 띄울 rect도 없다(부모
// 탭을 전환하면 그 지점부터 자연 재구성).
//
// 이 파일은 트리 계산만 담당(순수) — rect/DOM/스타일은 BranchSlotTabsOverlay가 처리한다.

import type { ComposerNode, BranchNode, BranchSlot } from '@safetyhub/shared';
import { walkNodeTree } from './nodeTreeWalk';

/** 탭 바 안의 슬롯 탭 1개(라벨은 slotLabel 규칙으로 계산됨). */
export interface BranchSlotTab {
  /** 슬롯 고유 ID(React key). */
  id: string;
  /** 탭 표시 라벨(label → 조건 요약 → "분기 N"). */
  label: string;
}

/** branch 1개의 탭 바 — 그 노드 rect 바로 위에 뜨며, 그 branch의 슬롯만 담는다. */
export interface BranchTabBar {
  /** 이 바가 대표하는 branch 노드 ID(rect 조회 + onSelectSlot/onSelectBranch 대상). */
  branchId: string;
  /** 슬롯 탭 목록(branch.branches 순서). */
  slots: BranchSlotTab[];
  /** 현재 활성 슬롯 인덱스(범위 밖은 0으로 clamp). */
  activeIndex: number;
}

/** 슬롯 라벨 truncate(탭이 과하게 길어지지 않게). */
export function truncate(text: string, max = 22): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** 슬롯 탭 표시 라벨 — label 우선 → 조건 요약(else/조건) → "분기 N"(미설정). */
export function slotLabel(slot: BranchSlot, index: number): string {
  const label = slot.label?.trim();
  if (label) return truncate(label);
  if (slot.condition === undefined) return 'else';
  const c = slot.condition.trim();
  return c === '' ? `분기 ${index + 1}` : truncate(c);
}

/**
 * activeBranchSlots에서 branch의 활성 슬롯 인덱스를 범위 clamp(기본 0).
 *
 * 파라미터를 구조적으로 잡아 **본문(양식) 탭의 `BranchNode`와 작성 탭의 `BranchWidget`이
 * 같은 규칙을 공유**한다(`writeBranchTabs.activeSlotIndex`가 이걸 다시 내보낸다). 두 축의
 * 슬롯 라벨은 폴백이 달라 통합하면 안 되지만, "저장된 index를 어떻게 clamp하는가"는 같은
 * 규칙이라 갈라져 있을 이유가 없었다.
 */
export function activeIndexOf(
  branch: { id: string; branches: readonly unknown[] },
  active: Record<string, number>,
): number {
  const raw = active[branch.id] ?? 0;
  return raw >= 0 && raw < branch.branches.length ? raw : 0;
}

/**
 * nodes 안의 "직속" branch를 수집한다(다른 branch를 건너지 않음).
 * group/stack children · repeat.template · table cell.content 는 재귀하되, branch를 만나면 그 branch를
 * 수집하고 **내부로는 더 내려가지 않는다**(그 branch가 다음 레벨의 경계). 따라서 body에 적용하면
 * 최상위 branch들이, 활성 슬롯 children에 적용하면 그 슬롯의 다음 레벨 branch들이 나온다.
 */
function findChildBranches(nodes: ComposerNode[]): BranchNode[] {
  const out: BranchNode[] = [];
  // walkNodeTree 기본 하강(group/stack·cell.content·repeat.template 전부)을 쓰되, branch를 만나면
  // 수집하고 그 하위(슬롯 children)로는 내려가지 않는다(visit false = 하위 스킵) — 그 branch가 다음
  // 레벨의 경계이므로. 자체 재귀 대신 공용 프리미티브로 일원화한다.
  walkNodeTree(nodes, (n) => {
    if (n.kind !== 'branch') return;
    out.push(n);
    return false;
  });
  return out;
}

/** branch → 탭 바 변환(슬롯 탭 + 활성 인덱스). */
function tabBarOf(branch: BranchNode, active: Record<string, number>): BranchTabBar {
  return {
    branchId: branch.id,
    slots: branch.branches.map((s, i) => ({ id: s.id, label: slotLabel(s, i) })),
    activeIndex: activeIndexOf(branch, active),
  };
}

/**
 * body 트리에서 "캔버스에 렌더되는" branch를 전부 훑어 branch별 탭 바를 만든다(최상위·중첩 무관).
 * 깊이 우선 — 부모를 먼저 담고 그 활성 슬롯 안의 중첩 branch를 이어서. 겹침 밀어내기가 "위(부모)를
 * 고정하고 아래(자식)를 민다"를 top 동률 시 **입력 순서**로 결정하므로 이 순서가 계약의 일부다.
 *
 * ⚠️ 열거 범위는 branchSlotLinks(F9 연결선)와 **같아야** 한다 — 탭이 없으면 선을 그을 출발점도,
 * ▸ 표식을 붙일 자리도 없다. 두 함수가 각자 재귀하므로 그 동치는 테스트가 지킨다.
 * seen 집합은 방어(순환/중복 방지).
 */
export function renderedBranchTabBars(
  body: ComposerNode[],
  active: Record<string, number>,
): BranchTabBar[] {
  const out: BranchTabBar[] = [];
  const seen = new Set<string>();
  const walk = (branches: BranchNode[]): void => {
    for (const branch of branches) {
      if (seen.has(branch.id)) continue;
      seen.add(branch.id);
      out.push(tabBarOf(branch, active));
      const activeSlot = branch.branches[activeIndexOf(branch, active)];
      if (activeSlot) walk(findChildBranches(activeSlot.children));
    }
  };
  walk(findChildBranches(body));
  return out;
}

// ────────────────────────────────────────────────────────────────
// 슬롯 → 중첩 branch 연결(분기 관계 연결선 오버레이용)
// ────────────────────────────────────────────────────────────────
//
// 연결선은 "이 슬롯을 고르면 저 분기가 나온다"를 캔버스 문맥에서 보여준다: 슬롯 탭에서 그 슬롯
// children 안의 중첩 branch 노드로 선을 긋는다.
//
// ⚠️ 비대칭이 하나 있고, 그게 이 함수가 존재하는 이유다. **선은 활성 슬롯에만 그을 수 있다** —
// 편집 캔버스는 활성 슬롯의 children만 렌더하므로 비활성 슬롯 속 branch는 DOM에 없고 rect가
// 없다. 하지만 "저 안에 분기가 더 있다"는 사실 자체는 JSON에 있으므로, 비활성 슬롯에는 선 대신
// 표식(▸)을 붙여 암시할 수 있다. 그래서 childBranchIds는 활성/비활성 **양쪽 모두** 채운다.
//
// 열거 범위는 "탭이 화면에 있는 branch"와 같다(= renderedBranchTabBars가 도달하는 활성 경로) —
// 탭이 없으면 선을 그을 출발점도, 표식을 붙일 자리도 없다.

/** 슬롯 1개의 연결 정보(순수 — DOM 미접촉). */
export interface BranchSlotLink {
  /** 이 슬롯이 속한 branch 노드 ID. */
  branchId: string;
  /** branch.branches 안에서의 인덱스(탭 앵커 `data-slot-tab="<branchId>::<slotIndex>"`와 짝). */
  slotIndex: number;
  /** 슬롯 고유 ID(React key). */
  slotId: string;
  /** 소속 branch의 현재 활성 슬롯인가 — false면 그 안 children은 캔버스에 없다(선 불가). */
  isActiveSlot: boolean;
  /**
   * 이 슬롯 children 안의 **직속** 중첩 branch id들. 더 깊은 중첩은 그 branch 자신의 링크가
   * 이어받으므로 여기서 평탄화하지 않는다(선이 한 단계씩 이어져 계층이 보인다).
   */
  childBranchIds: string[];
}

/** branch 목록에서 재귀 수집 — 활성 슬롯 안으로만 내려간다(비활성 속 branch는 탭이 없다). */
function collectLinksFrom(
  branches: BranchNode[],
  active: Record<string, number>,
  out: BranchSlotLink[],
): void {
  for (const branch of branches) {
    const activeIndex = activeIndexOf(branch, active);
    branch.branches.forEach((slot, i) => {
      const childBranches = findChildBranches(slot.children);
      out.push({
        branchId: branch.id,
        slotIndex: i,
        slotId: slot.id,
        isActiveSlot: i === activeIndex,
        childBranchIds: childBranches.map((c) => c.id),
      });
      // 활성 슬롯 안의 branch만 캔버스에 렌더되므로 그쪽으로만 재귀한다.
      if (i === activeIndex) collectLinksFrom(childBranches, active, out);
    });
  }
}

/**
 * body에서 "화면에 탭이 있는" 모든 branch의 슬롯별 연결 정보를 만든다.
 * 최상위 branch가 없으면 빈 배열. 순수 함수(DOM 미접촉).
 */
export function branchSlotLinks(
  body: ComposerNode[],
  active: Record<string, number>,
): BranchSlotLink[] {
  const out: BranchSlotLink[] = [];
  collectLinksFrom(findChildBranches(body), active, out);
  return out;
}
